import { Button, ButtonVariant } from '@Core/Components'
import { Loader } from '@Core/Components/Loader/Loader'
import { PurchasePreparation } from '@Purchasing/Domain/Model/PurchaseProcess'
import { useMemo, useState } from 'react'
import {
    BudgetSourceItemRow,
    chipClass,
    miniSelectClass,
    PurchaseBatchSummary,
    PurchaseDraftGroupCard,
    UnassignedBudgetItems,
} from './purchase-draft-group.parts'
import { DraftGroup, OrganizerItem } from './organizer.types'

const toItem = (si: any): OrganizerItem => ({
    sourceItemExternalId: si.sourceItemExternalId,
    description: si.description,
    sku: si.sku,
    quantity: Number(si.quantity ?? 0),
    unitPrice: si.unitPrice ?? null,
})

let tempSeq = 0
const nextTempId = () => `group-${Date.now()}-${++tempSeq}`

interface BudgetItemOrganizerProps {
    preparation: PurchasePreparation
    creating?: boolean
    onConfirm: (groups: DraftGroup[]) => void
}

/**
 * Fase 2.2 — "Organizar itens para compra" (polida na Fase 2.2.1).
 * Mostra TODOS os itens do orçamento, aplica a sugestão determinística e
 * permite reorganizar livremente. Estado único aqui; cards são apenas
 * visuais e recebem props/callbacks.
 */
export const BudgetItemOrganizer = ({
    preparation,
    creating,
    onConfirm,
}: BudgetItemOrganizerProps) => {
    const { sourceContext, existingProcessMappings, suggestedGroups } =
        preparation

    const buildInitialGroups = (): DraftGroup[] =>
        (suggestedGroups ?? []).map((g) => ({
            temporaryId: g.key,
            title: g.title,
            quantity: g.suggestedQuantity,
            items: g.items.map(toItem),
        }))

    const [groups, setGroups] = useState<DraftGroup[]>(buildInitialGroups)
    const [ungrouped, setUngrouped] = useState<OrganizerItem[]>([])
    const [pendingExcludedConfirm, setPendingExcludedConfirm] = useState(false)

    const boundByItemId = useMemo(() => {
        const map = new Map()
        for (const m of existingProcessMappings ?? []) {
            map.set(m.sourceItemExternalId, m)
        }
        return map
    }, [existingProcessMappings])

    const totalBudgetItems = sourceContext?.items?.length ?? 0
    const activeGroups = groups.filter((g) => g.items.length > 0)

    const resetSuggestion = () => {
        setGroups(buildInitialGroups())
        setUngrouped([])
        setPendingExcludedConfirm(false)
    }

    const updateGroup = (temporaryId: string, patch: Partial<DraftGroup>) => {
        setGroups((prev) =>
            prev.map((g) =>
                g.temporaryId === temporaryId ? { ...g, ...patch } : g
            )
        )
    }

    const removeFromGroup = (temporaryId: string, itemId: string) => {
        setGroups((prev) =>
            prev.map((g) =>
                g.temporaryId === temporaryId
                    ? {
                          ...g,
                          items: g.items.filter(
                              (i) => i.sourceItemExternalId !== itemId
                          ),
                      }
                    : g
            )
        )
        const item = groups
            .find((g) => g.temporaryId === temporaryId)
            ?.items.find((i) => i.sourceItemExternalId === itemId)
        if (item) setUngrouped((prev) => [...prev, item])
    }

    const deleteGroup = (temporaryId: string) => {
        const group = groups.find((g) => g.temporaryId === temporaryId)
        if (!group) return
        setGroups((prev) => prev.filter((g) => g.temporaryId !== temporaryId))
        setUngrouped((prev) => [...prev, ...group.items])
    }

    const moveItemToGroup = (
        fromGroupId: string | null,
        itemId: string,
        target: 'new' | string
    ) => {
        const sourceList = fromGroupId
            ? groups.find((g) => g.temporaryId === fromGroupId)?.items ?? []
            : ungrouped
        const item = sourceList.find((i) => i.sourceItemExternalId === itemId)
        if (!item) return

        if (fromGroupId) {
            setGroups((prev) =>
                prev.map((g) =>
                    g.temporaryId === fromGroupId
                        ? {
                              ...g,
                              items: g.items.filter(
                                  (i) => i.sourceItemExternalId !== itemId
                              ),
                          }
                        : g
                )
            )
        } else {
            setUngrouped((prev) =>
                prev.filter((i) => i.sourceItemExternalId !== itemId)
            )
        }

        if (target === 'new') {
            setGroups((prev) => [
                ...prev,
                {
                    temporaryId: nextTempId(),
                    title: '',
                    quantity: item.quantity || null,
                    items: [item],
                },
            ])
            return
        }
        setGroups((prev) =>
            prev.map((g) =>
                g.temporaryId === target
                    ? { ...g, items: [...g.items, item] }
                    : g
            )
        )
    }

    const handleCreateClick = () => {
        if (activeGroups.length === 0) return
        if (ungrouped.length > 0 && !pendingExcludedConfirm) {
            // §20/§19 — confirmação explícita quando há itens de fora
            setPendingExcludedConfirm(true)
            return
        }
        onConfirm(activeGroups)
    }

    // §31 — empty state claro
    if (totalBudgetItems === 0) {
        return (
            <section
                className="mt-4 rounded-lg border border-border p-6 text-center text-sm text-muted-foreground"
                data-testid="item-organizer-empty"
            >
                Este orçamento não possui itens para organizar.
            </section>
        )
    }

    return (
        <section className="mt-6" data-testid="item-organizer">
            {/* Cabeçalho do organizador */}
            <div className="mb-4 flex flex-col gap-1 border-b border-border pb-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-foreground">
                        Organizar itens para compra
                    </h2>
                    <p className="text-sm text-muted-foreground dark:text-muted-foreground">
                        Orçamento{' '}
                        <strong className="font-semibold">
                            #{sourceContext?.budget?.code}
                        </strong>{' '}
                        · {totalBudgetItems}{' '}
                        {totalBudgetItems === 1
                            ? 'item encontrado'
                            : 'itens encontrados'}{' '}
                        · {suggestedGroups?.length ?? 0}{' '}
                        {(suggestedGroups?.length ?? 0) === 1
                            ? 'grupo sugerido'
                            : 'grupos sugeridos'}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <span className="hidden text-xs italic text-muted-foreground lg:inline">
                        Agrupamento sugerido automaticamente — ajuste livremente
                    </span>
                    <Button
                        variant={ButtonVariant.secondary}
                        onClick={resetSuggestion}
                    >
                        Restaurar sugestão
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
                {/* Coluna esquerda: grupos + não organizados */}
                <div className="space-y-4">
                    {groups.length === 0 && ungrouped.length > 0 && (
                        <p className="text-sm italic text-muted-foreground">
                            Nenhum grupo ativo — adicione os itens abaixo a um
                            novo processo.
                        </p>
                    )}

                    {groups.map((group, index) => (
                        <PurchaseDraftGroupCard
                            key={group.temporaryId}
                            index={index + 1}
                            group={group}
                            suggested={isSuggestedGroup(group.temporaryId)}
                            boundByItemId={boundByItemId}
                            otherGroups={groups
                                .filter(
                                    (g) => g.temporaryId !== group.temporaryId
                                )
                                .map((g) => ({
                                    temporaryId: g.temporaryId,
                                    title: g.title,
                                }))}
                            onTitleChange={(title) =>
                                updateGroup(group.temporaryId, { title })
                            }
                            onQuantityChange={(quantity) =>
                                updateGroup(group.temporaryId, { quantity })
                            }
                            onMoveItem={(itemId, target) =>
                                moveItemToGroup(
                                    group.temporaryId,
                                    itemId,
                                    target
                                )
                            }
                            onRemoveItem={(itemId) =>
                                removeFromGroup(group.temporaryId, itemId)
                            }
                            onDeleteGroup={() => deleteGroup(group.temporaryId)}
                        />
                    ))}

                    <UnassignedBudgetItems
                        items={ungrouped}
                        groups={groups.map((g) => ({
                            temporaryId: g.temporaryId,
                            title: g.title,
                        }))}
                        onMoveItem={(itemId, target) =>
                            moveItemToGroup(null, itemId, target)
                        }
                    />

                    {groups.length === 0 && ungrouped.length === 0 && (
                        <Loader />
                    )}
                </div>

                {/* Coluna direita: resumo sticky em desktop */}
                <div className="lg:sticky lg:top-4">
                    <PurchaseBatchSummary
                        activeGroups={activeGroups}
                        totalBudgetItems={totalBudgetItems}
                        unassignedCount={ungrouped.length}
                        creating={creating}
                        pendingExcludedConfirm={pendingExcludedConfirm}
                        onCreateClick={handleCreateClick}
                        onCancelExcluded={() =>
                            setPendingExcludedConfirm(false)
                        }
                        onConfirmExcluded={handleCreateClick}
                    />
                </div>
            </div>
        </section>
    )
}

function isSuggestedGroup(temporaryId: string) {
    return /^(PRODUCT|SKU|NAME|ITEM):/.test(temporaryId)
}
